import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { getSwaggerServiceBaseUrl } from "../../config/apiBase";
import ModulePage from "../../shared-ui/ModulePage";
import { EmptyState, ErrorState, Skeleton } from "../../shared-ui/States";
import "../../shared-ui/crane-tokens.css";

const token = () => localStorage.getItem("zTaskAuthToken") || "";

function normalizeTasks(payload) {
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.tasks)) return payload.tasks;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.message)) return payload.message;
  return [];
}

function statusOf(task) {
  return String(task?.status || task?.taskStatus || task?.current_status || "unknown")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "_");
}

function titleOf(task) {
  return task?.title || task?.taskName || task?.name || `Task #${task?.taskId || task?.id || "?"}`;
}

export default function TaskDashboardPage() {
  const [tasks, setTasks] = useState([]);
  const [busy, setBusy] = useState(true);
  const [err, setErr] = useState("");

  const load = async () => {
    setBusy(true);
    setErr("");
    try {
      const res = await fetch(`${getSwaggerServiceBaseUrl()}/api/get-task`, {
        headers: { Authorization: `Bearer ${token()}`, "X-Client": "web" },
      });
      const body = await res.json().catch(() => ({}));
      // Legacy API returned 404 when empty — treat as empty list, not an error.
      if (res.status === 404) {
        setTasks([]);
        return;
      }
      if (!res.ok) throw new Error(body.message || `HTTP ${res.status}`);
      setTasks(normalizeTasks(body));
    } catch (e) {
      setErr(e.message || "Failed to load tasks");
      setTasks([]);
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const kpis = useMemo(() => {
    const counts = {
      total: tasks.length,
      open: 0,
      wip: 0,
      completed: 0,
      overdue: 0,
    };
    const now = Date.now();
    for (const task of tasks) {
      const st = statusOf(task);
      if (st === "completed" || st === "closed" || st === "done") counts.completed += 1;
      else if (st === "wip" || st === "in_progress" || st === "in-progress") counts.wip += 1;
      else counts.open += 1;

      const due = task?.dueDate || task?.due_date;
      if (due && !["completed", "closed", "done"].includes(st)) {
        const dueMs = new Date(due).getTime();
        if (!Number.isNaN(dueMs) && dueMs < now) counts.overdue += 1;
      }
    }
    return counts;
  }, [tasks]);

  const recent = useMemo(() => tasks.slice(0, 8), [tasks]);

  const cards = [
    { key: "total", label: "Total tasks", href: "/tasks" },
    { key: "open", label: "Open", href: "/tasks" },
    { key: "wip", label: "In progress", href: "/tasks" },
    { key: "completed", label: "Completed", href: "/tasks" },
    { key: "overdue", label: "Overdue", href: "/tasks" },
  ];

  return (
    <ModulePage
      crumbs={["Operations", "ZTask", "Dashboard"]}
      title="ZTask dashboard"
      description="Live task counts for your tenant. Use Tasks for the full workboard."
      actions={
        <>
          <button type="button" className="crane-btn" onClick={load} disabled={busy}>
            Refresh
          </button>
          <Link to="/tasks/create" className="crane-btn crane-btn--primary" style={{ textDecoration: "none" }}>
            + Create Task
          </Link>
        </>
      }
    >
      {busy && <Skeleton rows={5} height={48} />}
      {err && <ErrorState title="Dashboard unavailable" description={err} onRetry={load} />}

      {!busy && !err && (
        <>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))",
              gap: 16,
              marginTop: 20,
            }}
          >
            {cards.map((c) => (
              <Link
                key={c.key}
                to={c.href}
                style={{
                  background: "#fff",
                  border: "1px solid #e2e8f0",
                  borderRadius: 8,
                  padding: 16,
                  textDecoration: "none",
                  color: "inherit",
                }}
              >
                <div style={{ color: "#64748b", fontSize: 13 }}>{c.label}</div>
                <div style={{ fontSize: 28, fontWeight: 700, color: "#152a34", marginTop: 8 }}>
                  {Number(kpis[c.key] || 0)}
                </div>
              </Link>
            ))}
          </div>

          <h3 style={{ marginTop: 28, marginBottom: 8 }}>Recent tasks</h3>
          {recent.length === 0 ? (
            <EmptyState
              title="No tasks yet"
              description="Create a task to populate this dashboard."
              action={
                <Link to="/tasks/create" className="crane-btn crane-btn--primary" style={{ textDecoration: "none" }}>
                  Create Task
                </Link>
              }
            />
          ) : (
            <table style={{ width: "100%", borderCollapse: "collapse", background: "#fff" }}>
              <thead>
                <tr style={{ textAlign: "left", borderBottom: "2px solid #e2e8f0" }}>
                  <th style={{ padding: 8 }}>Task</th>
                  <th style={{ padding: 8 }}>Status</th>
                  <th style={{ padding: 8 }}>Priority</th>
                </tr>
              </thead>
              <tbody>
                {recent.map((task) => (
                  <tr key={task.taskId || task.id || titleOf(task)} style={{ borderBottom: "1px solid #e2e8f0" }}>
                    <td style={{ padding: 8 }}>
                      <Link to={`/tasks/${task.taskId || task.id}`}>{titleOf(task)}</Link>
                    </td>
                    <td style={{ padding: 8 }}>{statusOf(task).replace(/_/g, " ")}</td>
                    <td style={{ padding: 8 }}>{task.priority || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}
    </ModulePage>
  );
}
